import React from 'react';
import { Upload, Trash2 } from '../Icons';
import { ProposalCompanyInfo, ProposalDiscount, ProposalVat, ProposalPaymentTerms, ProposalTerms, ProposalColors } from '../../types';

interface Props {
    t: any;
    colors: ProposalColors;
    companyInfo: ProposalCompanyInfo;
    setCompanyInfo: (info: ProposalCompanyInfo) => void;
    discount: ProposalDiscount;
    setDiscount: (discount: ProposalDiscount) => void;
    vat: ProposalVat;
    setVat: (vat: ProposalVat) => void;
    paymentTerms: ProposalPaymentTerms;
    setPaymentTerms: (terms: ProposalPaymentTerms) => void;
    terms: ProposalTerms;
    setTerms: (terms: ProposalTerms) => void;
    handleLogoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    saveCompanyInfo: () => void;
    handleColorChange: (key: keyof ProposalColors, value: string) => void;
    isAdmin: boolean;
    adminPanel: any;
    handleAdminEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    addAllowedUser: () => void;
    removeAllowedUser: (email: string) => void;
}

export const ProposalSettings: React.FC<Props> = ({
    t, colors, companyInfo, setCompanyInfo,
    discount, setDiscount, vat, setVat,
    paymentTerms, setPaymentTerms, terms, setTerms,
    handleLogoUpload, saveCompanyInfo, handleColorChange,
    isAdmin, adminPanel, handleAdminEmailChange, addAllowedUser, removeAllowedUser
}) => {
    const ColorControl = ({ label, colorKey }: { label: string, colorKey: keyof ProposalColors }) => (
        <div className="flex items-center gap-2">
            <label className="text-sm w-32" style={{ color: colors.textSecondary }}>{label}:</label>
            <input type="color" value={colors[colorKey]} onChange={(e) => handleColorChange(colorKey, e.target.value)} className="w-8 h-8 p-0 border-none rounded cursor-pointer" />
            <input type="text" value={colors[colorKey]} onChange={(e) => handleColorChange(colorKey, e.target.value)} className="w-24 px-2 py-1 border border-gray-300 rounded text-sm" />
        </div>
    );

    return (
        <div className="border-t-2 pt-4 mt-4 print:hidden" style={{ borderColor: colors.primary }}>
            <h3 className="text-lg font-semibold mb-3">{t.settings}</h3>

            <div className="grid grid-cols-2 gap-6">
                {/* Company Info */}
                <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3">{t.companyInfo}</h4>
                    <div className="space-y-2">
                        <input type="text" placeholder={t.company} value={companyInfo.name} onChange={(e) => setCompanyInfo({ ...companyInfo, name: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                        <input type="email" placeholder={t.email} value={companyInfo.email} onChange={(e) => setCompanyInfo({ ...companyInfo, email: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                        <input type="tel" placeholder={t.phone} value={companyInfo.phone} onChange={(e) => setCompanyInfo({ ...companyInfo, phone: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                        <input type="text" placeholder={t.address} value={companyInfo.address} onChange={(e) => setCompanyInfo({ ...companyInfo, address: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                        <input type="text" placeholder={t.website} value={companyInfo.website} onChange={(e) => setCompanyInfo({ ...companyInfo, website: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                        <label className="flex items-center gap-2 px-3 py-2 bg-gray-200 rounded-md cursor-pointer text-sm">
                            <Upload /> {t.uploadLogo}
                            <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                        </label>
                    </div>
                </div>

                {/* Bank Details */}
                <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3">{t.bankDetails}</h4>
                    <div className="space-y-2">
                        <input type="text" placeholder={t.bankAccount} value={companyInfo.bankAccount} onChange={(e) => setCompanyInfo({ ...companyInfo, bankAccount: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                        <input type="text" placeholder={t.bankName} value={companyInfo.bankName} onChange={(e) => setCompanyInfo({ ...companyInfo, bankName: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                        <input type="text" placeholder={t.companyCode} value={companyInfo.companyCode} onChange={(e) => setCompanyInfo({ ...companyInfo, companyCode: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                        <input type="text" placeholder={t.vatCode} value={companyInfo.vatCode} onChange={(e) => setCompanyInfo({ ...companyInfo, vatCode: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" />
                        <button onClick={saveCompanyInfo} className="w-full px-3 py-2 bg-green-600 text-white rounded-md text-sm">{t.save}</button>
                    </div>
                </div>

                {/* Discount & VAT */}
                <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3">{t.discount}</h4>
                    <div className="space-y-2">
                        <label className="flex items-center gap-2">
                            <input type="checkbox" checked={discount.enabled} onChange={(e) => setDiscount({ ...discount, enabled: e.target.checked })} />
                            <span className="text-sm">{t.enableDiscount}</span>
                        </label>
                        {discount.enabled && (
                            <>
                                <select value={discount.type} onChange={(e) => setDiscount({ ...discount, type: e.target.value as any })} className="w-full px-3 py-2 border rounded-md text-sm">
                                    <option value="percentage">{t.percentage}</option>
                                    <option value="fixed">{t.fixedAmount}</option>
                                </select>
                                <input type="number" value={discount.value} onChange={(e) => setDiscount({ ...discount, value: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-md text-sm" placeholder="0" />
                            </>
                        )}
                    </div>
                    <div className="mt-4 pt-4 border-t">
                        <h4 className="font-semibold mb-3">PVM</h4>
                        <label className="flex items-center gap-2 mb-2">
                            <input type="checkbox" checked={vat.enabled} onChange={(e) => setVat({ ...vat, enabled: e.target.checked })} />
                            <span className="text-sm">{t.enableVat}</span>
                        </label>
                        {vat.enabled && (
                            <input type="number" value={vat.rate} onChange={(e) => setVat({ ...vat, rate: parseFloat(e.target.value) || 21 })} className="w-full px-3 py-2 border rounded-md text-sm" placeholder="21" />
                        )}
                    </div>
                </div>

                {/* Payment Terms */}
                <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3">{t.paymentSchedule}</h4>
                    <label className="flex items-center gap-2 mb-2">
                        <input type="checkbox" checked={paymentTerms.enabled} onChange={(e) => setPaymentTerms({ ...paymentTerms, enabled: e.target.checked })} />
                        <span className="text-sm">{t.enablePaymentSchedule}</span>
                    </label>
                    {paymentTerms.enabled && (
                        <div className="space-y-2 text-sm">
                            {paymentTerms.stages.map((stage, idx) => (
                                <div key={stage.id} className="flex items-center gap-2">
                                    <input type="text" value={stage.name} onChange={(e) => {
                                        const updated = [...paymentTerms.stages];
                                        updated[idx].name = e.target.value;
                                        setPaymentTerms({ ...paymentTerms, stages: updated });
                                    }} className="flex-1 px-2 py-1 border rounded text-xs" />
                                    <input type="number" value={stage.percentage} onChange={(e) => {
                                        const updated = [...paymentTerms.stages];
                                        updated[idx].percentage = parseFloat(e.target.value) || 0;
                                        setPaymentTerms({ ...paymentTerms, stages: updated });
                                    }} className="w-16 px-2 py-1 border rounded text-xs" />
                                    <span>%</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Color Settings */}
                <div className="border rounded-lg p-4 col-span-2">
                    <h4 className="font-semibold mb-3">{t.colorSettings}</h4>
                    <div className="grid grid-cols-3 gap-4">
                        <ColorControl label={t.primaryColor} colorKey="primary" />
                        <ColorControl label={t.successColor} colorKey="success" />
                        <ColorControl label={t.costColor} colorKey="cost" />
                        <ColorControl label={t.infoColor} colorKey="info" />
                        <ColorControl label={t.textColor} colorKey="textPrimary" />
                        <ColorControl label={t.backgroundColor} colorKey="background" />
                    </div>
                </div>

                {/* Terms */}
                <div className="border rounded-lg p-4 col-span-2">
                    <h4 className="font-semibold mb-3">{t.termsAndConditions}</h4>
                    <div className="space-y-2">
                        <textarea value={terms.payment} onChange={(e) => setTerms({ ...terms, payment: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" rows={2} placeholder={t.termsAndConditions} />
                        <textarea value={terms.validity} onChange={(e) => setTerms({ ...terms, validity: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" rows={2} placeholder={t.proposalValidity} />
                        <textarea value={terms.additional} onChange={(e) => setTerms({ ...terms, additional: e.target.value })} className="w-full px-3 py-2 border rounded-md text-sm" rows={2} placeholder={t.additionalTerms} />
                    </div>
                </div>

                {/* ADMIN PANEL */}
                {isAdmin && (
                    <div className="border rounded-lg p-4 col-span-2 bg-yellow-50">
                        <h4 className="font-semibold mb-3 text-yellow-800">{t.adminPanelTitle}</h4>

                        {/* Add new user */}
                        <div className="mb-4">
                            <h5 className="font-semibold mb-2">{t.addNewUser}</h5>
                            <div className="flex gap-2">
                                <input
                                    type="email"
                                    placeholder={t.addUserPlaceholder}
                                    value={adminPanel.newUserEmail}
                                    onChange={handleAdminEmailChange}
                                    className="w-full px-3 py-2 border rounded-md text-sm"
                                />
                                <button
                                    onClick={addAllowedUser}
                                    className="px-4 py-2 bg-green-600 text-white rounded-md text-sm"
                                >
                                    {t.addUserButton}
                                </button>
                            </div>
                        </div>

                        {/* Allowed users list */}
                        <div>
                            <h5 className="font-semibold mb-2">{t.authorizedUsers}</h5>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {adminPanel.allowedUsers.length === 0 ? (
                                    <p className="text-sm text-gray-600">{t.noUsersYet}</p>
                                ) : (
                                    adminPanel.allowedUsers.map((u: any) => (
                                        <div key={u.id} className="flex justify-between items-center p-2 bg-white border rounded-md">
                                            <span className="text-sm">{u.id}</span>
                                            <button
                                                onClick={() => removeAllowedUser(u.id)}
                                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                            >
                                                <Trash2 />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
